/**
 * ReadinessPage — ISO 27001 Foundational Readiness Exam
 * Landing page: lists past assessments and starts new ones.
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { readinessApi } from '../services/api';
import { useSwal } from '../hooks/useSwal';
import { useToast } from '../hooks/useToast';
import { PageHeader } from '../components/PageHeader';
import { NimbusTable } from '../components/NimbusTable';
import { SortableTh } from '../components/SortableTh';
import { useSortableData } from '../hooks/useSortableData';
import { usePagination } from '../hooks/usePagination';
import { Pagination } from '../components/Pagination';
import { type Tone, toneFromMap } from '../constants/tone';

type Assessment = {
  assessmentId: string;
  title: string;
  status: 'draft' | 'completed';
  createdAt: string;
  submittedAt?: string;
  stage?: string;
  stageName?: string;
  normalisedScore?: string | number;
};

type SortField = 'title' | 'status' | 'stage' | 'score' | 'date';

const STAGE_COLOURS: Record<string, Tone> = {
  initial: 'danger',
  emerging: 'warning',
  structured: 'info',
  audit_ready: 'success',
};

export const ReadinessPage: React.FC = () => {
  const queryClient = useQueryClient();
  const swal = useSwal();
  const toast = useToast();
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['readiness-assessments'],
    queryFn: () => readinessApi.list(),
  });

  const createMutation = useMutation({
    mutationFn: (title: string) => readinessApi.create({ title }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['readiness-assessments'] });
      const id = result.assessment?.assessmentId;
      if (id) {
        window.location.href = `/readiness/${id}`;
      }
    },
    onError: () => toast.error('Failed to create assessment'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => readinessApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['readiness-assessments'] });
      toast.success('Assessment deleted');
    },
    onError: () => toast.error('Failed to delete assessment'),
  });

  const handleCreate = () => {
    const title = newTitle.trim() || `ISO 27001 Readiness — ${new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`;
    setCreating(false);
    setNewTitle('');
    createMutation.mutate(title);
  };

  const handleDelete = async (id: string) => {
    const confirmed = await swal.confirmDelete('this assessment');
    if (confirmed) {
      deleteMutation.mutate(id);
    }
  };

  const assessments: Assessment[] = data?.assessments || [];

  const { sorted, field: sortField, dir: sortDir, toggle: toggleSort } = useSortableData<Assessment, SortField>(
    assessments,
    (a, f) => {
      switch (f) {
        case 'title':  return a.title;
        case 'status': return a.status;
        case 'stage':  return a.stageName || '';
        case 'score':  return a.normalisedScore != null ? Number(a.normalisedScore) : -1;
        case 'date': {
          const d = a.status === 'completed' && a.submittedAt ? a.submittedAt : a.createdAt;
          return new Date(d).getTime();
        }
        default: return '';
      }
    },
    'title',
  );
  const { pageItems, page, setPage, totalPages, pageSize, totalItems } = usePagination(sorted, 25);

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">
      <PageHeader
        icon="ri-award-line"
        title="ISO 27001 Readiness"
        subtitle="25-question structural assessment across 8 ISO 27001 domains."
        colour="success"
        actions={
          <button
            className="np-btn np-btn-primary"
            onClick={() => setCreating(true)}
            disabled={createMutation.isPending}
          >
            <i className="ri-add-line"></i>
            {createMutation.isPending ? 'Creating…' : 'New Assessment'}
          </button>
        }
      />

      {/* Info banner */}
      <div className="np-grid np-grid-4">
        <div className="np-card">
          <div className="np-card-body np-stat">
            <span className="np-stat-icon np-tone-primary"><i className="ri-survey-line"></i></span>
            <p className="np-stat-label">Total Assessments</p>
            <p className="np-stat-value np-tabular">{assessments.length}</p>
          </div>
        </div>
        <div className="np-card">
          <div className="np-card-body np-stat">
            <span className="np-stat-icon np-tone-success"><i className="ri-checkbox-circle-line"></i></span>
            <p className="np-stat-label">Completed</p>
            <p className="np-stat-value np-tabular">{assessments.filter(a => a.status === 'completed').length}</p>
          </div>
        </div>
        <div className="np-card">
          <div className="np-card-body np-stat">
            <span className="np-stat-icon np-tone-warning"><i className="ri-time-line"></i></span>
            <p className="np-stat-label">In Progress</p>
            <p className="np-stat-value np-tabular">{assessments.filter(a => a.status !== 'completed').length}</p>
          </div>
        </div>
        <div className="np-card">
          <div className="np-card-body np-stat">
            <span className="np-stat-icon np-tone-info"><i className="ri-question-answer-line"></i></span>
            <p className="np-stat-label">Questions / Assessment</p>
            <p className="np-stat-value np-tabular">25</p>
          </div>
        </div>
      </div>

      {/* Inline create form */}
      {creating && (
        <div className="np-card">
          <div className="np-card-head">
            <h2><i className="ri-add-circle-line"></i> New Assessment</h2>
          </div>
          <div className="np-card-body">
            <div className="np-field">
              <label>Assessment Name</label>
              <div className="d-flex gap-2">
                <input
                  type="text"
                  className="np-input"
                  placeholder={`ISO 27001 Readiness — ${new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`}
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                  autoFocus
                />
                <button className="np-btn np-btn-primary" onClick={handleCreate}>
                  <i className="ri-play-line"></i>Start
                </button>
                <button className="np-btn np-btn-ghost" onClick={() => { setCreating(false); setNewTitle(''); }}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assessment List */}
      <div className="np-card">
        <div className="np-card-head">
          <h2><i className="ri-list-check-2"></i> Assessments</h2>
          <span className="np-chip np-tone-neutral np-tabular">{assessments.length}</span>
        </div>

        {isLoading && (
          <div className="np-card-body text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading…</span>
            </div>
          </div>
        )}

        {error && (
          <div className="np-card-body">
            <div className="np-banner np-tone-danger">
              <i className="ri-error-warning-line"></i>
              Failed to load assessments.
            </div>
          </div>
        )}

        {!isLoading && !error && assessments.length === 0 && (
          <div className="np-card-body text-center py-5">
            <i className="ri-award-line ri-2x d-block mb-3" style={{ color: 'var(--bs-success)' }}></i>
            <h5 className="mb-1">Start your first assessment</h5>
            <p className="np-sub mb-3">Get a structural readiness score across 8 ISO 27001 domains.</p>
            <button className="np-btn np-btn-primary" onClick={() => setCreating(true)}>
              <i className="ri-add-line"></i>New Assessment
            </button>
          </div>
        )}

        {!isLoading && assessments.length > 0 && (
          <NimbusTable thead={<tr>
              <SortableTh field="title" active={sortField === 'title'} dir={sortDir} onSort={toggleSort}>Assessment</SortableTh>
              <SortableTh field="status" active={sortField === 'status'} dir={sortDir} onSort={toggleSort}>Status</SortableTh>
              <SortableTh field="stage" active={sortField === 'stage'} dir={sortDir} onSort={toggleSort}>Stage</SortableTh>
              <SortableTh field="score" active={sortField === 'score'} dir={sortDir} onSort={toggleSort}>Score</SortableTh>
              <SortableTh field="date" active={sortField === 'date'} dir={sortDir} onSort={toggleSort}>Date</SortableTh>
              <th></th>
            </tr>}>
                {pageItems.map((a) => {
                  const colour = toneFromMap(STAGE_COLOURS, a.stage, 'neutral');
                  const score = a.normalisedScore != null ? Number(a.normalisedScore) : null;
                  const date = a.status === 'completed' && a.submittedAt
                    ? new Date(a.submittedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                    : new Date(a.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

                  return (
                    <tr key={a.assessmentId}>
                      <td><span className="np-row-title-static">{a.title}</span></td>
                      <td>
                        {a.status === 'completed'
                          ? <span className="np-chip np-tone-success"><i className="ri-checkbox-circle-line"></i>Completed</span>
                          : <span className="np-chip np-tone-warning"><i className="ri-time-line"></i>In Progress</span>}
                      </td>
                      <td>
                        {a.stageName
                          ? <span className={`np-chip np-tone-${colour}`}>{a.stageName}</span>
                          : <span style={{ color: 'var(--bs-secondary-color)' }}>—</span>}
                      </td>
                      <td>
                        {score != null
                          ? <span className={`np-chip np-tone-${colour} np-tabular`}>{score.toFixed(1)}/100</span>
                          : <span style={{ color: 'var(--bs-secondary-color)' }}>—</span>}
                      </td>
                      <td className="np-tabular">{date}</td>
                      <td className="text-end">
                        <div className="d-flex gap-2 justify-content-end">
                          {a.status === 'completed' ? (
                            <a href={`/readiness/${a.assessmentId}/results`} className="np-btn np-btn-ghost np-btn-sm">
                              <i className="ri-bar-chart-line"></i>Results
                            </a>
                          ) : (
                            <a href={`/readiness/${a.assessmentId}`} className="np-btn np-btn-primary np-btn-sm">
                              <i className="ri-play-line"></i>Continue
                            </a>
                          )}
                          <button
                            className="np-icon-btn np-icon-btn-danger"
                            onClick={() => handleDelete(a.assessmentId)}
                            disabled={deleteMutation.isPending}
                            title="Delete"
                          >
                            <i className="ri-delete-bin-line"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
          </NimbusTable>
        )}
        {pageItems.length > 0 && (
          <Pagination page={page} totalPages={totalPages} pageSize={pageSize} totalItems={totalItems} onPageChange={setPage} />
        )}
      </div>

      <p className="np-sub">
        <i className="ri-information-line me-1"></i>
        Structural readiness indicator only — not a compliance assessment. Scores should be interpreted by a qualified ISO 27001 Lead Implementer.
      </p>
    </div>
  );
};
